<template>
  <div v-html="year.turn_in_still_text"></div>
  <v-card class="fill-height info_card" title="Team Video">
    <v-card-text>
      <AssetPreviewComponent
        :is-modal="false"
        :asset="teamVideo"
        type="video"
        :onUpload="handleTeamVideoFileUpload"
        @asset-saved="handleAssetSaved"></AssetPreviewComponent>

      <!-- HLS Generation Progress Indicator -->
      <div v-if="isGeneratingHls" class="mt-4 d-flex flex-column align-center">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <p class="text-center text-caption mt-2 text-primary">
          Generating HLS stream for optimized playback...
        </p>
      </div>
    </v-card-text>
  </v-card>
  <v-card class="fill-height info_card" title="Video Still">
    <v-card-text>
      <AssetPreviewComponent
        :is-modal="false"
        :asset="videoStill"
        type="image"
        :onUpload="handleTeamStillFileUpload"
        @asset-saved="handleAssetSaved"></AssetPreviewComponent>
    </v-card-text>
  </v-card>

  <v-card class="fill-height info_card" title="Before the 72 - Happy 21 Video">
    <v-card-text>
      <AssetPreviewComponent
        :is-modal="false"
        :asset="bts"
        type="video"
        :onUpload="handleBTSFileUpload"
        @asset-saved="handleAssetSaved"></AssetPreviewComponent>
    </v-card-text>
  </v-card>
</template>
<script setup lang="ts">
import AssetPreviewComponent from '@/components/ui/AssetPreviewComponent.vue';
import { uploadScreenShot, uploadTeamVideo, uploadBTSVideo } from '@/controllers';
import { ITeamCriteriaDTO, IYearDTO, ITeamDTO, IAssetDTO, TeamCriteriaDTO } from '@/dtos';
import { useSubmissionnStore } from '@/stores';
import { computed, ref } from 'vue';

interface Props {
  registration: ITeamCriteriaDTO;
  year: IYearDTO;
  team: ITeamDTO;
}

const props = defineProps<Props>();
const year = computed(() => props.year);
const team = computed(() => props.team);
const registration = computed(() => props.registration);
const submission = computed(() => registration.value.submissions[0] ?? new TeamCriteriaDTO());
const submissionStore = useSubmissionnStore();

const teamVideoHls = ref<IAssetDTO | null>(null);
const isGeneratingHls = ref<boolean>(false);
const teamVideo = computed<IAssetDTO | undefined>(() => {
  // First try the HLS version if we have it
  if (teamVideoHls.value) {
    return teamVideoHls.value;
  }
  // Otherwise fall back to the original team video from registration
  return registration.value.submissions[0]?.teamVideo;
});

const bts = computed<IAssetDTO | undefined>(() => {
  return registration.value.submissions[0]?.bts;
});

const videoStill = computed(() => {
  return registration.value.submissions[0]?.screenshot;
});

const handleTeamVideoFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadTeamVideo(
    team.value.id,
    year.value.id,
    submission.value.id,
    file,
    updateProgress,
  );
  /*
  // retrieve hls video stream, which can take some time
  const { id } = response;

  // Start HLS generation loading indicator
  isGeneratingHls.value = true;

  try {
    const hlsAsset = await getVideoHlsStream(id);

    // Debug logging to see what we got
    console.log('hlsAsset received:', hlsAsset);
    console.log('hlsAsset hls_url:', hlsAsset?.hls_url);
    // console.log('hlsAsset hasHlsUrl():', hlsAsset?.hasHlsUrl());

    // update the local ref to show the new video
    teamVideoHls.value = hlsAsset;

    // Debug the computed value after setting
    console.log('teamVideo computed after setting:', teamVideo.value);
    console.log('teamVideo computed hls_url:', teamVideo.value?.hls_url);

    if (hlsAsset) {
      console.log('HLS stream ready, updating submission store');

      // Update the registration data with the HLS asset
      if (registration.value.submissions[0]) {
        registration.value.submissions[0].teamVideo = hlsAsset;
      }

      // Invalidate the store to trigger a refresh and ensure the updated data is persisted
      await handleAssetSaved();

      // Optional: If the submission store has a specific update method, you could call it here
      // For example: await submissionStore.updateTeamVideo(submission.value.id, hlsAsset);
    }

    console.log('Got HLS stream for video', hlsAsset);
  } catch (error) {
    console.error('Error generating HLS stream:', error);
    // Continue without HLS - the video will still work with the original file
  } finally {
    // Stop HLS generation loading indicator
    isGeneratingHls.value = false;
  }

  if (!id) throw new Error('No asset ID returned from upload');
  */
  return response;
};
const handleBTSFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadBTSVideo(
    team.value.id,
    year.value.id,
    submission.value.id,
    file,
    updateProgress,
  );
  return response;
};

const handleTeamStillFileUpload = async (
  file: File,
  updateProgress: (progress: number) => void,
): Promise<IAssetDTO> => {
  const response = await uploadScreenShot(
    team.value.id,
    year.value.id,
    submission.value.id,
    file,
    updateProgress,
  );
  return response;
};

const handleAssetSaved = async () => {
  await submissionStore.invalidateMyCriteriaSelections();
};
</script>
